import { useCallback, useMemo, useState } from 'react'
import { menu, MenuItem } from '../data/menu'

export interface CartLine {
  item: MenuItem
  quantity: number
}

export function useCart() {
  const [quantities, setQuantities] = useState<Record<string, number>>({})

  const add = useCallback((id: string) => {
    setQuantities((prev) => ({ ...prev, [id]: Math.min((prev[id] ?? 0) + 1, 20) }))
  }, [])

  const remove = useCallback((id: string) => {
    setQuantities((prev) => {
      const next = (prev[id] ?? 0) - 1
      const copy = { ...prev }
      if (next <= 0) {
        delete copy[id]
      } else {
        copy[id] = next
      }
      return copy
    })
  }, [])

  const clear = useCallback(() => setQuantities({}), [])

  const lines = useMemo<CartLine[]>(
    () =>
      Object.entries(quantities)
        .map(([id, quantity]) => {
          const item = menu.find((m) => m.id === id)
          return item ? { item, quantity } : null
        })
        .filter((line): line is CartLine => line !== null),
    [quantities],
  )

  const count = useMemo(() => lines.reduce((sum, line) => sum + line.quantity, 0), [lines])

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.item.price, 0),
    [lines],
  )

  return { quantities, lines, count, total, add, remove, clear }
}
